<template>
  <app-page
    :title="$t('employee.enroll.pageTitle')"
    :previous="[
      { label: $t('employee.previousLabel'), to: { name: 'employees' } },
      {
        label: employee ? employee.full_name : $t('employee.title'),
        to: { name: 'employee', params: { employeeId: $route.params.employeeId } },
      },
    ]"
  >
    <template v-if="!hasLaunched" v-slot:after-header>
      <sme-alert level="warning" class="mt-3">
        <i18n
          :path="
            portalUpdatesJan2026Enabled ? 'employee.enroll.setupWarningActions' : 'employee.enroll.setupWarningTasks'
          "
          tag="span"
        >
          <template #warningLabel
            ><strong>{{ $t('employee.enroll.warningLabel') }}</strong></template
          >
        </i18n>
      </sme-alert>
    </template>

    <template v-if="!loading && employee && employee.employee_id" v-slot:header>
      <b-button
        variant="outline-primary"
        :to="{ name: 'employee-deactivate', params: { employeeId: employee.employee_id } }"
      >
        <font-awesome-icon :icon="['fad', 'user-minus']" class="mr-2" />{{ $t('employee.deactivate.disableButton') }}
      </b-button>
    </template>

    <template v-slot:infobox>
      <sme-info-box :title="$t('employee.enroll.aboutTitle')" id="about-enrollment" save-state>
        <p>{{ $t('employee.enroll.aboutBody1') }}</p>
        <p>
          <i18n path="employee.enroll.aboutBody2" tag="span">
            <template #integrationLink
              ><router-link :to="{ name: 'sme-integrations' }">{{
                $t('employee.enroll.integrationLink')
              }}</router-link></template
            >
          </i18n>
        </p>
        <p>
          <strong>{{ $t('employee.enroll.processIntroBold') }}</strong>
        </p>
        <ol>
          <li>{{ $t('employee.enroll.step1') }}</li>
          <li>
            {{
              $t('employee.enroll.step2', {
                accountNumberLabel: getBankingInfo.accountNumber.label,
                sortCodeLabel: getBankingInfo.sortCode.label,
              })
            }}
          </li>
          <li>{{ $t('employee.enroll.step3') }}</li>
        </ol>
        <p>
          <i18n path="employee.enroll.paydayBody" tag="span">
            <template #paydayLink
              ><router-link :to="{ name: 'sme-pay-day' }">{{ $t('employee.enroll.paydayLink') }}</router-link></template
            >
          </i18n>
        </p>
      </sme-info-box>
      <sme-info-box :title="$t('employee.enroll.codeTitle')" id="employee-code" save-state>
        <p>
          <i18n path="employee.enroll.codeBody1" tag="span">
            <template #employeeCodeBold
              ><strong>{{ $t('employee.enroll.employeeCodeBold') }}</strong></template
            >
          </i18n>
        </p>
        <p>
          <i18n path="employee.enroll.codeBody2" tag="span">
            <template #shiftsLink
              ><router-link :to="{ name: 'shifts' }">{{ $t('employee.enroll.shiftsLink') }}</router-link></template
            >
          </i18n>
        </p>
        <p>
          <i18n path="employee.enroll.codeBody3" tag="span">
            <template #integrationLink
              ><router-link :to="{ name: 'sme-integrations' }">{{
                $t('employee.enroll.integrationLink')
              }}</router-link></template
            >
          </i18n>
        </p>
      </sme-info-box>
    </template>

    <app-loading :loading="loading" />
    <template v-if="!loading">
      <app-input
        v-model="employee.work_email"
        type="text"
        name="full name"
        :label="$t('admins.add.emailLabel')"
        readonly
      ></app-input>

      <ValidationObserver v-slot="v">
        <b-form @submit.prevent="v.handleSubmit(onSubmit)">
          <app-input
            v-model="employee.employee_code"
            type="text"
            name="employee code"
            rules="required"
            :label="$t('employee.employeeCodeLabel')"
          ></app-input>

          <app-input
            v-model="employee_name"
            type="text"
            name="full name"
            :rules="{ required: true, regex: NAME_REGEX }"
            :placeholder="$t('employee.enroll.fullNamePlaceholder')"
            :description="$t('employee.enroll.fullNameDescription')"
            :label="$t('employee.fullNameLabel')"
          ></app-input>

          <app-input
            v-model="setup.salary_or_hourly"
            type="radio"
            name="salary or hourly"
            rules="required"
            :label="$t('employee.howPaidLabel')"
            :options="salaryOrHourlyOptions"
            :description="$t('employee.enroll.howPaidDescription')"
            buttons
            button-variant="outline-primary"
          ></app-input>
          <template v-if="setup.salary_or_hourly == EMPLOYEE_PAY_TYPE.HOURLY && canSetDefaultWagerate">
            <app-input
              v-model="setup.default_wagerate"
              type="number"
              name="Rate"
              step="0.01"
              :rules="WAGERATE_VALIDATION_RULES"
              :label="$t('employee.enroll.hourlyRateLabel', { currency: default_currency })"
            ></app-input>
          </template>

          <template v-if="setup.salary_or_hourly == EMPLOYEE_PAY_TYPE.SALARY">
            <app-input
              v-model="setup.salary"
              type="number"
              name="Salary"
              step="0.01"
              :rules="SALARY_VALIDATION_RULES"
              :label="$t('employee.enroll.grossSalaryLabel', { currency: default_currency })"
            ></app-input>
            <app-input
              v-model="setup.salary_period"
              type="radio"
              name="Salary Period"
              rules="required"
              :label="$t('employee.perLabel')"
              :options="salaryPeriodOptions"
              :description="$t('employee.enroll.salaryPeriodDescription')"
            ></app-input>
            <template v-if="canSetDefaultWagerate">
              <app-input
                v-model="setup.default_wagerate"
                type="number"
                name="Rate"
                step="0.01"
                :rules="Object.assign({}, WAGERATE_VALIDATION_RULES, { required: false })"
                :label="$t('employee.enroll.hourlyRateLabel', { currency: default_currency })"
              ></app-input>
            </template>
            <app-input
              v-model="setup.started_on"
              type="date"
              name="Start Date"
              rules="required"
              :label="$t('employee.startDateLabel')"
            ></app-input>
            <app-input
              v-model="setup.salary_accrual_schedule"
              type="select"
              name="Accrual Schedule"
              rules="required"
              :undefined-label="$t('employee.selectAccrualScheduleLabel')"
              :options="salaryAccrualSchedules"
              :label="$t('employee.accrualScheduleLabel')"
            >
            </app-input>
          </template>

          <app-input
            v-model="setup.sort_code"
            type="text"
            :name="getBankingInfo.sortCode.label"
            :placeholder="getBankingInfo.sortCode.placeholder"
            :rules="{ required: true, regex: getBankingInfo.sortCode.regex }"
            :label="getBankingInfo.sortCode.label"
            :description="getBankingInfo.sortCode.description"
            :readonly="!canUpdateBanking"
          ></app-input>

          <app-input
            v-model="setup.account_number"
            type="text"
            :name="getBankingInfo.accountNumber.label"
            :placeholder="getBankingInfo.accountNumber.placeholder"
            :rules="{ required: true, regex: getBankingInfo.accountNumber.regex }"
            :label="getBankingInfo.accountNumber.label"
            :description="getBankingInfo.accountNumber.description"
            :readonly="!canUpdateBanking"
          ></app-input>

          <app-input
            v-if="canSetAccountType"
            v-model="setup.account_type"
            type="select"
            :name="getBankingInfo.accountType.label"
            :placeholder="getBankingInfo.accountType.placeholder"
            :label="getBankingInfo.accountType.label"
            :description="getBankingInfo.accountType.description"
            :options="getBankingInfo.accountType.options"
          ></app-input>

          <app-input
            v-if="!isDisablePayscheduleEditing"
            v-model="paySchedule"
            type="select"
            name="Pay Schedule"
            :options="paySchedules"
            rules="required"
            :label="$t('employees.filters.payScheduleLabel')"
            :description="$t('employee.enroll.paySchedulesDescription')"
            key="pay_schedule_id"
          ></app-input>

          <page-sub-footer>
            <template #before>
              <sme-alert v-if="error" level="danger">{{ error }}</sme-alert>
            </template>

            <b-button
              type="submit"
              variant="primary"
              :disabled="v.invalid || saving || !hasLaunched || isInPayrollBlackout"
            >
              <b-spinner v-if="saving" class="mr-2" small />
              {{ saving ? $t('common.submittingButton') : $t('common.submitButton') }}
            </b-button>
          </page-sub-footer>
          <!-- TODO we may actually want to use the CAN_ENROLL_EMPLOYEES feature flag here -->
          <span v-if="!hasLaunched" class="d-block mt-2 text-danger">{{
            $t('employee.enroll.mustFinishOnboardingBody')
          }}</span>
          <span v-else-if="isInPayrollBlackout" class="d-block mt-2 text-danger">{{
            $t('employee.enroll.payrollBlackoutBody')
          }}</span>
        </b-form>
      </ValidationObserver>
    </template>
  </app-page>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import ApiClient from '@/ApiClient';
import { getBankingInfo } from '@/Banking';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppPage from '@/components/AppPage.vue';
import PageSubFooter from '@/components/PageSubFooter.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import SmeInfoBox from '@/components/atoms/SmeInfoBox.vue';
import IntegrationsState from '@/state/IntegrationsState';
import State from '@/state/State';
import {
  EMPLOYEE_PAY_TYPE,
  SALARY_VALIDATION_RULES,
  WAGERATE_VALIDATION_RULES,
  NAME_REGEX,
  updateEmployeeAndBanking,
} from '@/utils/Employee';
import useFeatureFlags from '@/composables/useFeatureFlags';
import { employeeIsOnPayscheduleInBlackout } from '@/utils/Payschedule';

const defaultSetup = {
  worked_on: undefined,
  employee_id: null,
  hours: undefined,
  rate: undefined,

  salary_accrual_schedule: undefined,

  sort_code: undefined,
  account_number: undefined,
  account_type: undefined,

  // SME defaults
  salary_or_hourly: EMPLOYEE_PAY_TYPE.HOURLY,
  salary: undefined,
  salary_period: 'year',
  default_wagerate: undefined,
  started_on: undefined,
};

export default {
  name: 'EnrollEmployee',
  components: { ValidationObserver, AppInput, AppLoading, AppPage, PageSubFooter, SmeAlert, SmeInfoBox },
  data() {
    return {
      EMPLOYEE_PAY_TYPE,
      NAME_REGEX,
      SALARY_VALIDATION_RULES,
      WAGERATE_VALIDATION_RULES,
      loading: true,
      error: undefined,
      saving: false,
      setup: { ...defaultSetup },
      employee: undefined,
      state: State.state,
      integrationsState: IntegrationsState,
      hasLaunched: false,
      isInPayrollBlackout: false,

      newSortCode: undefined,
      newAccountNumber: undefined,
      employee_name: undefined,
      paySchedule: null,
    };
  },
  methods: {
    onSubmit() {
      this.error = undefined;
      this.saving = true;
      this.updateEmployeeSalary();
    },
    async updateEmployeeSalary() {
      if (this.employee_name) {
        this.employee.full_name = this.employee_name;
      }
      const newEmployee = { ...this.employee };

      if (this.setup.salary_or_hourly === EMPLOYEE_PAY_TYPE.HOURLY) {
        newEmployee.default_wagerate = this.setup.default_wagerate * 100; // Convert to pence
        newEmployee.salary_properties = {
          salary_or_hourly: EMPLOYEE_PAY_TYPE.HOURLY,
          default_wagerate: newEmployee.default_wagerate,
        };
      } else if (this.setup.salary_or_hourly === EMPLOYEE_PAY_TYPE.SALARY) {
        newEmployee.salary_properties = {
          salary_or_hourly: EMPLOYEE_PAY_TYPE.SALARY,
          salary: Math.round(this.setup.salary),
          salary_period: this.setup.salary_period.toUpperCase(),
          salary_accrual_schedule: this.setup.salary_accrual_schedule,
        };
        // Check if newEmployee.default_wagerate is not null
        if (this.setup.default_wagerate !== null) {
          const defaultWageRate = this.setup.default_wagerate * 100; // Convert to pence
          newEmployee.default_wagerate = defaultWageRate;
          newEmployee.salary_properties.default_wagerate = defaultWageRate;
        }
      }

      if (this.paySchedule) {
        newEmployee.pay_schedule_id = this.paySchedule;
      }

      this.employee = {
        ...this.employee,
        ...newEmployee,
        properties: {
          beneficiary_account_number: this.setup.account_number,
          beneficiary_sort_code: this.setup.sort_code,
        },
        started_on: this.setup.started_on,
      };

      // Only companies with the canSetAccountType feature flag can set the account type
      if (this.setup.account_type) {
        this.employee.properties.beneficiary_type = this.setup.account_type;
      }

      try {
        await updateEmployeeAndBanking(this.employee);
      } catch (error) {
        this.error = error.message;
        this.loading = false;
        this.saving = false;
        return;
      }

      await this.updateEmployeeState();
    },
    async updateEmployeeState() {
      const newState = {
        state: 'ENROLLING',
        banking_info: {
          sort_code: this.setup.sort_code,
          account_number: this.setup.account_number,
          // Account type is optional, should only be set if the feature flag is enabled
          account_type: this.setup.account_type,
        },
      };

      try {
        await ApiClient.updateEmployeeState(this.employee.employee_id, newState);
        await ApiClient.getUser();
        this.$router.push({
          name: 'employee-activate',
          params: { employeeId: this.employee.employee_id },
        });
      } catch (error) {
        this.onError(error);
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const employee = await ApiClient.getEmployee(this.$route.params.employeeId);
        this.employee = employee;
        this.employee_name = employee.full_name;

        if (employee.salary_properties) {
          this.setup = {
            ...this.setup,
            salary_or_hourly: employee.salary_properties.salary_or_hourly,
            salary: employee.salary_properties.salary,
            salary_period: (employee.salary_properties.salary_period || this.setup.salary_period).toLowerCase(),
            salary_accrual_schedule: this.state?.company?.properties?.default_salary_accrual_schedule || 'WEEKDAY0917',
          };
        }

        if (employee.default_wagerate) {
          this.setup.default_wagerate = employee.default_wagerate / 100;
        }

        this.setup.started_on = employee.started_on;
        this.default_currency = employee.default_currency ?? 'GBP';

        if (employee.properties?.beneficiary_account_number) {
          // Banking is setup
          this.setup.account_number = employee.properties.beneficiary_account_number;
          this.setup.sort_code = employee.properties.beneficiary_sort_code;
          this.setup.account_type = employee.properties.beneficiary_type;
        } else if (employee.integration_properties?.personal_account_number) {
          // Already synced from an integration
          this.setup.account_number = employee.integration_properties.personal_account_number;
          this.setup.sort_code = employee.integration_properties.personal_sort_code;
        } else if (employee.properties?.sme_properties?.personal_account_number) {
          // Already stored as part of pay with Stream
          this.setup.account_number = employee.properties.sme_properties.personal_account_number;
          this.setup.sort_code = employee.properties.sme_properties.personal_sort_code;
        }

        const paySchedulesResponse = await ApiClient.getCompanyPaySchedules(this.state.company.company_id);
        this.paySchedules = paySchedulesResponse.data.map(x => {
          return { text: x.name, value: x.pay_schedule_id };
        });
        this.paySchedule = employee.pay_schedule_id;

        this.loading = false;
      } catch (error) {
        this.onError(error);
      }
    },
    onError(error) {
      this.error = error?.message || error;
      this.loading = false;
      this.saving = false;
    },
  },
  async mounted() {
    this.hasLaunched = this.state?.company?.properties?.is_soft_launch !== true;
    await this.loadData();
    this.isInPayrollBlackout = await employeeIsOnPayscheduleInBlackout(this.employee);
  },
  computed: {
    getBankingInfo: () => getBankingInfo(),
    salaryOrHourlyOptions() {
      return [
        { value: EMPLOYEE_PAY_TYPE.SALARY, text: this.$t('employee.salaryOption') },
        { value: EMPLOYEE_PAY_TYPE.HOURLY, text: this.$t('employee.shiftOption') },
      ];
    },
    salaryPeriodOptions() {
      return [
        { value: 'year', text: this.$t('employee.enroll.yearOption') },
        { value: 'month', text: this.$t('employee.enroll.monthOption') },
      ];
    },
    salaryAccrualSchedules() {
      return [
        { value: 'WEEKDAY0917', text: this.$t('employee.weekdaysScheduleOption') },
        {
          value: 'EVERYDAY0917',
          text: this.$t('employee.everydayScheduleOption'),
        },
      ];
    },
  },
  setup() {
    const {
      canSetAccountType,
      canSetDefaultWagerate,
      canUpdateBanking,
      isDisablePayscheduleEditing,
      portalUpdatesJan2026Enabled,
    } = useFeatureFlags();

    return {
      canSetAccountType,
      canSetDefaultWagerate,
      canUpdateBanking,
      isDisablePayscheduleEditing,
      portalUpdatesJan2026Enabled,
    };
  },
};
</script>
